import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { checklistInstanceKeys } from '@/lib/queryKeys';
import type {
  ChecklistInstance,
  ChecklistItemPhoto,
  ChecklistItemValue,
  ChecklistOfflineUpdate,
  ChecklistSnapshot,
  ChecklistVisibility,
} from '@/types/settings';

export function useAppointmentChecklists(appointmentId: string | null | undefined) {
  return useApiQuery<ChecklistInstance[]>({
    queryKey: checklistInstanceKeys.byAppointment(appointmentId ?? ''),
    queryFn: async () => {
      const res = await apiGet<ChecklistInstance[] | { data: ChecklistInstance[] }>(
        operationsClient,
        settingsEndpoints.appointmentChecklists(appointmentId!),
      );
      return Array.isArray(res) ? res : (res?.data ?? []);
    },
    enabled: !!appointmentId,
    showErrorToast: false,
  });
}

export function useJobChecklistReports(jobId: string | null | undefined) {
  return useApiQuery<ChecklistInstance[]>({
    queryKey: checklistInstanceKeys.jobReports(jobId ?? ''),
    queryFn: async () =>
      apiGet<ChecklistInstance[]>(operationsClient, settingsEndpoints.jobChecklistReports(jobId!)),
    enabled: !!jobId,
    showErrorToast: false,
  });
}

export function useChecklistSnapshot(appointmentId: string | null | undefined) {
  return useApiQuery<ChecklistSnapshot>({
    queryKey: checklistInstanceKeys.snapshot(appointmentId ?? ''),
    queryFn: () =>
      apiGet<ChecklistSnapshot>(
        operationsClient,
        settingsEndpoints.appointmentChecklistSnapshot(appointmentId!),
      ),
    enabled: !!appointmentId,
    showErrorToast: false,
  });
}

export function useSyncChecklistSnapshot(
  appointmentId: string,
  options?: { showSuccessToast?: boolean; showErrorToast?: boolean },
) {
  const queryClient = useQueryClient();
  return useApiMutation<ChecklistInstance[], { updates: ChecklistOfflineUpdate[] }>({
    mutationFn: ({ updates }) =>
      apiPost<ChecklistInstance[], { updates: ChecklistOfflineUpdate[] }>(
        operationsClient,
        settingsEndpoints.appointmentChecklistSync(appointmentId),
        { updates },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: checklistInstanceKeys.byAppointment(appointmentId),
      });
      void queryClient.invalidateQueries({
        queryKey: checklistInstanceKeys.snapshot(appointmentId),
      });
      if (options?.showSuccessToast !== false) {
        toast.success('Checklist changes saved');
      }
    },
    showErrorToast: options?.showErrorToast,
  });
}

function offlineSnapshotKey(appointmentId: string) {
  return `checklist-snapshot:${appointmentId}`;
}

function offlineQueueKey(appointmentId: string) {
  return `checklist-sync-queue:${appointmentId}`;
}

function readStoredJson<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredJson<T>(key: string, value: T) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

function offlineItemKey(instanceId: string, sectionIdx: number, itemIdx: number) {
  return `${instanceId}:${sectionIdx}:${itemIdx}`;
}

function applyOfflineUpdates(
  instances: ChecklistInstance[],
  updates: ChecklistOfflineUpdate[],
): ChecklistInstance[] {
  if (updates.length === 0) return instances;
  const updateMap = new Map(
    updates.map((update) => [
      offlineItemKey(update.instanceId, update.sectionIdx, update.itemIdx),
      update,
    ]),
  );

  return instances.map((instance) => ({
    ...instance,
    sections: instance.sections.map((section, sectionIdx) => ({
      ...section,
      items: section.items.map((item, itemIdx) => {
        const update = updateMap.get(offlineItemKey(instance.id, sectionIdx, itemIdx));
        return update
          ? {
              ...item,
              value: update.value,
            }
          : item;
      }),
    })),
  }));
}

export function useChecklistOfflineWorkflow(
  appointmentId: string,
  serverInstances: ChecklistInstance[] | undefined,
) {
  const snapshot = useChecklistSnapshot(appointmentId);
  const sync = useSyncChecklistSnapshot(appointmentId, {
    showSuccessToast: false,
    showErrorToast: false,
  });
  const [cachedInstances, setCachedInstances] = useState<ChecklistInstance[]>(() =>
    readStoredJson<ChecklistInstance[]>(offlineSnapshotKey(appointmentId), []),
  );
  const [queuedUpdates, setQueuedUpdates] = useState<ChecklistOfflineUpdate[]>(() =>
    readStoredJson<ChecklistOfflineUpdate[]>(offlineQueueKey(appointmentId), []),
  );
  // The queue is immutable: a replacement edit creates a new batch even when
  // its item key and timestamp match. Only pause the exact batch that failed.
  const [failedBatch, setFailedBatch] = useState<ChecklistOfflineUpdate[] | null>(null);
  const [syncErrorMessage, setSyncErrorMessage] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== 'undefined' && !navigator.onLine,
  );

  useEffect(() => {
    setCachedInstances(readStoredJson<ChecklistInstance[]>(offlineSnapshotKey(appointmentId), []));
    setQueuedUpdates(readStoredJson<ChecklistOfflineUpdate[]>(offlineQueueKey(appointmentId), []));
    setFailedBatch(null);
    setSyncErrorMessage(null);
  }, [appointmentId]);

  useEffect(() => {
    if (!serverInstances) return;
    setCachedInstances(serverInstances);
    writeStoredJson(offlineSnapshotKey(appointmentId), serverInstances);
  }, [appointmentId, serverInstances]);

  useEffect(() => {
    if (!snapshot.data?.instances) return;
    setCachedInstances(snapshot.data.instances);
    writeStoredJson(offlineSnapshotKey(appointmentId), snapshot.data.instances);
  }, [appointmentId, snapshot.data]);

  useEffect(() => {
    writeStoredJson(offlineQueueKey(appointmentId), queuedUpdates);
  }, [appointmentId, queuedUpdates]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const updateOnlineState = () => {
      const offlineNow = !window.navigator.onLine;
      setIsOffline((wasOffline) => {
        if (wasOffline && !offlineNow) {
          setFailedBatch(null);
          setSyncErrorMessage(null);
        }
        return offlineNow;
      });
    };
    window.addEventListener('online', updateOnlineState);
    window.addEventListener('offline', updateOnlineState);
    updateOnlineState();
    return () => {
      window.removeEventListener('online', updateOnlineState);
      window.removeEventListener('offline', updateOnlineState);
    };
  }, []);

  useEffect(() => {
    if (isOffline || queuedUpdates.length === 0 || sync.isPending) return;
    if (failedBatch === queuedUpdates) return;
    sync.mutate(
      { updates: queuedUpdates },
      {
        onSuccess: (instances) => {
          // A save only acknowledges the edits it sent. Other items (or a newer
          // edit to the same item) may have been queued while it was in flight.
          // Compare the immutable update objects, since timestamps can collide.
          setQueuedUpdates((current) =>
            current.filter((update) => !queuedUpdates.includes(update)),
          );
          setCachedInstances(instances);
          setFailedBatch(null);
          setSyncErrorMessage(null);
          writeStoredJson(offlineSnapshotKey(appointmentId), instances);
        },
        onError: (error) => {
          setFailedBatch(queuedUpdates);
          setSyncErrorMessage(error.message);
        },
      },
    );
  }, [appointmentId, failedBatch, isOffline, queuedUpdates, sync]);

  const committedInstances = serverInstances ?? cachedInstances;
  const instances = useMemo(
    () => applyOfflineUpdates(committedInstances, queuedUpdates),
    [committedInstances, queuedUpdates],
  );

  const pendingItemKeys = useMemo(
    () =>
      new Set(
        queuedUpdates.map((update) =>
          offlineItemKey(update.instanceId, update.sectionIdx, update.itemIdx),
        ),
      ),
    [queuedUpdates],
  );

  const queueItemUpdate = useCallback((update: Omit<ChecklistOfflineUpdate, 'timestamp'>) => {
    const nextUpdate: ChecklistOfflineUpdate = {
      ...update,
      timestamp: new Date().toISOString(),
    };
    const key = offlineItemKey(update.instanceId, update.sectionIdx, update.itemIdx);

    setQueuedUpdates((current) => [
      ...current.filter(
        (item) => offlineItemKey(item.instanceId, item.sectionIdx, item.itemIdx) !== key,
      ),
      nextUpdate,
    ]);
    setCachedInstances((current) => applyOfflineUpdates(current, [nextUpdate]));
  }, []);

  const isItemPending = useCallback(
    (instanceId: string, sectionIdx: number, itemIdx: number) =>
      pendingItemKeys.has(offlineItemKey(instanceId, sectionIdx, itemIdx)),
    [pendingItemKeys],
  );

  const hasSyncError = failedBatch === queuedUpdates && queuedUpdates.length > 0;

  const retrySync = useCallback(() => {
    setFailedBatch(null);
    setSyncErrorMessage(null);
  }, []);

  const discardQueue = useCallback(() => {
    setQueuedUpdates([]);
    setFailedBatch(null);
    setSyncErrorMessage(null);
    if (serverInstances) {
      setCachedInstances(serverInstances);
      writeStoredJson(offlineSnapshotKey(appointmentId), serverInstances);
    }
  }, [appointmentId, serverInstances]);

  return {
    instances,
    committedInstances,
    isOffline,
    queuedCount: queuedUpdates.length,
    syncPending: sync.isPending,
    syncError: hasSyncError ? (syncErrorMessage ?? 'Changes could not be saved.') : null,
    retrySync,
    discardQueue,
    queueItemUpdate,
    isItemPending,
  };
}

export function useUpdateChecklistItem(appointmentId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    ChecklistInstance,
    {
      instanceId: string;
      sectionIdx: number;
      itemIdx: number;
      value: ChecklistItemValue;
    }
  >({
    mutationFn: ({ instanceId, sectionIdx, itemIdx, value }) =>
      apiPut<ChecklistInstance, { value: ChecklistItemValue }>(
        operationsClient,
        settingsEndpoints.appointmentChecklistItem(appointmentId, instanceId, sectionIdx, itemIdx),
        { value },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: checklistInstanceKeys.byAppointment(appointmentId),
      });
    },
  });
}

export function useSetChecklistRepeatSelections(appointmentId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    ChecklistInstance,
    { instanceId: string; sectionDefId: string; selections: string[] }
  >({
    mutationFn: ({ instanceId, sectionDefId, selections }) =>
      apiPut<ChecklistInstance, { sectionDefId: string; selections: string[] }>(
        operationsClient,
        settingsEndpoints.appointmentChecklistRepeatSelections(appointmentId, instanceId),
        { sectionDefId, selections },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: checklistInstanceKeys.byAppointment(appointmentId),
      });
      toast.success('Sections configured');
    },
  });
}

export function useUpdateChecklistVisibility(appointmentId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<ChecklistInstance, { instanceId: string; visibility: ChecklistVisibility }>(
    {
      mutationFn: ({ instanceId, visibility }) =>
        apiPut<ChecklistInstance, { visibility: ChecklistVisibility }>(
          operationsClient,
          settingsEndpoints.appointmentChecklistVisibility(appointmentId, instanceId),
          { visibility },
        ),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: checklistInstanceKeys.byAppointment(appointmentId),
        });
      },
    },
  );
}

export function useCompleteChecklist(appointmentId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<ChecklistInstance, { instanceId: string }>({
    mutationFn: ({ instanceId }) =>
      apiPost<ChecklistInstance>(
        operationsClient,
        settingsEndpoints.appointmentChecklistComplete(appointmentId, instanceId),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: checklistInstanceKeys.byAppointment(appointmentId),
      });
      toast.success('Checklist completed');
    },
  });
}

export async function uploadChecklistPhotoFile(
  uploadUrl: string,
  file: File,
  rollback: () => Promise<unknown>,
): Promise<void> {
  try {
    const upload = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!upload.ok) throw new Error(`Upload failed (${upload.status})`);
  } catch (error) {
    // The API creates the attachment reference before returning the signed URL.
    // Remove it when the object upload fails so an orphaned reference cannot
    // satisfy a required-photo completion rule.
    try {
      await rollback();
    } catch {
      // Preserve the upload error; the failed cleanup can be retried separately.
    }
    throw error;
  }
}

export function useUploadChecklistPhoto(appointmentId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    ChecklistItemPhoto,
    {
      instanceId: string;
      sectionIdx: number;
      itemIdx: number;
      file: File;
      caption?: string;
    }
  >({
    mutationFn: async ({ instanceId, sectionIdx, itemIdx, file, caption }) => {
      const { uploadUrl, photo } = await apiPost<
        { uploadUrl: string; photo: ChecklistItemPhoto },
        { fileName: string; contentType: string; fileSize: number; caption?: string }
      >(
        operationsClient,
        settingsEndpoints.appointmentChecklistItemPhotos(
          appointmentId,
          instanceId,
          sectionIdx,
          itemIdx,
        ),
        {
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
          caption,
        },
      );

      await uploadChecklistPhotoFile(uploadUrl, file, () =>
        apiDelete<{ deleted: boolean }>(
          operationsClient,
          settingsEndpoints.appointmentChecklistItemPhoto(
            appointmentId,
            instanceId,
            sectionIdx,
            itemIdx,
            photo.id,
          ),
        ),
      );

      return photo;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: checklistInstanceKeys.byAppointment(appointmentId),
      });
      toast.success('Photo uploaded');
    },
  });
}

export function useDeleteChecklistPhoto(appointmentId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    { deleted: boolean },
    {
      instanceId: string;
      sectionIdx: number;
      itemIdx: number;
      photoId: string;
    }
  >({
    mutationFn: ({ instanceId, sectionIdx, itemIdx, photoId }) =>
      apiDelete<{ deleted: boolean }>(
        operationsClient,
        settingsEndpoints.appointmentChecklistItemPhoto(
          appointmentId,
          instanceId,
          sectionIdx,
          itemIdx,
          photoId,
        ),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: checklistInstanceKeys.byAppointment(appointmentId),
      });
      toast.success('Photo removed');
    },
  });
}
